import { useAuth } from '@clerk/clerk-react';
import {
  useMe,
  useOwnerPortalContact,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import {
  CalendarDatesIcon,
  CalendarEventIcon,
  ClipboardTextIcon,
  FileInvoiceIcon,
  FolderOpenIcon,
  GearIcon,
  HomeIcon,
} from '@vrplatform/icons';
import { day, utc } from '@vrplatform/utils';
import { Skeleton } from '@mantine/core';
import { getStatementYearlySummaryHref } from '@vrplatform/ui-common';
import { NavbarRoute } from '../layout/navbar';
import { getOwnerPortalSettings } from '../views/settings/SettingsFeaturesOwnerPortal';
import { NavigationSection } from './NavigationSection';
import { useLocation } from 'react-router';

const ICON_SIZE = 18;

export const OwnerNavigation = () => {
  const { actor } = useAuth();
  const [{ id: teamId, isGL: GL }] = useTeam();
  const { id: meId } = useMe();

  const { contactId } = useOwnerPortalContact();

  const location = useLocation();

  const isUserPreview = !!actor?.sub;

  const { data, isInitialLoading } = useQuery(
    (q, args) => {
      const settings = getOwnerPortalSettings(q, args.teamId);

      const isOwnerArchived = !!q
        .ownerUserAccesses({
          where: {
            userId: { _eq: args.meId },
            _or: [
              {
                contactId: { _eq: args.contactId },
                contact: {
                  status: { _eq: 'inactive' },
                },
              },
              {
                ownerId: { _eq: args.contactId },
                owner: {
                  status: { _eq: 'inactive' },
                },
              },
            ],
          },
        })
        .map((x) => x.id)[0];

      const statement = q
        .ownerStatements({
          where: {
            tenantId: { _eq: args.teamId },
            status: { _in: ['posted', 'published'] },
            owners: {
              newOwnerId: { _eq: args.contactId },
            },
          },
          limit: 1,
          order_by: [{ updatedAt: 'desc_nulls_last' }],
        })
        .map((statement) => ({
          startAt: statement.startAt,
        }))[0];

      return {
        showReservationSetting: !!settings.showReservationSettingId,
        statement,
        isOwnerArchived,
      };
    },
    {
      variables: { teamId, meId, contactId },
      skip: !teamId || !meId || !contactId,
      queryKey: ['settings', 'ownerStatements'],
    }
  );

  const date = data?.statement?.startAt || utc().yyyymmdd();
  const isArchived = data?.isOwnerArchived;
  const showReservations = !!data?.showReservationSetting && !isArchived;

  return (
    <>
      <NavigationSection>
        <NavbarRoute
          title="Statements"
          link="/statements"
          icon={<ClipboardTextIcon size={ICON_SIZE} />}
          end
          active={
            !GL
              ? undefined
              : location.pathname.startsWith('/statements/owner-statements') ||
                location.pathname.startsWith('/statement/')
          }
        />

        {!GL && contactId && (
          <NavbarRoute
            title="Summary"
            link={getStatementYearlySummaryHref({
              ownerId: contactId,
              date,
            })}
            icon={<FileInvoiceIcon size={ICON_SIZE} />}
            end
          />
        )}
        {GL && (
          <NavbarRoute
            title="Summary"
            link={`/statements/summary/${day().startOf('year').yyyymmdd()}...${day().endOf('year').yyyymmdd()}`}
            icon={<FileInvoiceIcon size={ICON_SIZE} />}
          />
        )}
      </NavigationSection>

      {isInitialLoading ? (
        <>
          <Skeleton h={30} opacity={0.1} />
          <Skeleton h={30} opacity={0.1} />
        </>
      ) : (
        !isArchived && (
          <>
            <NavigationSection>
              {(showReservations ||
                window.location.host.includes('localhost')) && (
                <>
                  <NavbarRoute
                    title="Calendar"
                    link="/calendar"
                    icon={<CalendarDatesIcon size={ICON_SIZE} />}
                  />
                  <NavbarRoute
                    title="Reservations"
                    link="/reservations"
                    icon={<CalendarEventIcon size={ICON_SIZE} />}
                  />
                </>
              )}

              {!GL && (
                <NavbarRoute
                  title="Listings"
                  link="/listings"
                  icon={<HomeIcon size={ICON_SIZE} />}
                />
              )}
            </NavigationSection>

            <NavigationSection>
              <NavbarRoute
                title="Files"
                link="/files"
                icon={<FolderOpenIcon size={ICON_SIZE} />}
              />
              {!isUserPreview && (
                <NavbarRoute
                  title="Settings"
                  link={!GL ? '/settings' : '/settings/details'}
                  icon={<GearIcon size={ICON_SIZE} />}
                />
              )}
            </NavigationSection>
          </>
        )
      )}
    </>
  );
};
